import React, { useMemo, useState } from "react";
import { Input, Select, Tag } from "@/components/kit";
import { fmtDateTime } from "@/lib/vda";

const SOURCE_LABEL = {
  relay: "Relay", system: "System", fw_f12: "FW F12 Import", email_ingestion: "Email ingestion",
  i247_ooh: "OOH · i247", technical_vor: "Vehicle Off Road", one_link: "1Link", daily_hire: "Daily hire",
  manual: "Manual",
};
const SOURCE_TONE = { fw_f12: "amber", email_ingestion: "clay", i247_ooh: "clay", technical_vor: "clay", relay: "green", system: "muted" };

// #8 Operationally useful vehicle history: chronological, source-aware, with
// search + type/source filtering. Historic FW F12 rows are always labelled.
export function VehicleHistory({ events = [], testId = "vehicle-history" }) {
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [source, setSource] = useState("");

  const types = useMemo(() => [...new Set(events.map((e) => e.event_type).filter(Boolean))].sort(), [events]);
  const sources = useMemo(() => [...new Set(events.map((e) => e.source).filter(Boolean))].sort(), [events]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return events.filter((e) => {
      if (type && e.event_type !== type) return false;
      if (source && e.source !== source) return false;
      if (ql) {
        const hay = `${e.title || ""} ${e.body || ""} ${e.raw_content || ""} ${e.category || ""} ${e.actor_label || ""}`.toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [events, q, type, source]);

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-2 mb-4">
        <Input data-testid="vh-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search history…" className="w-auto min-w-[180px] flex-1" />
        <Select data-testid="vh-filter-type" value={type} onChange={(e) => setType(e.target.value)} className="w-auto min-w-[150px]">
          <option value="">All event types</option>
          {types.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
        </Select>
        <Select data-testid="vh-filter-source" value={source} onChange={(e) => setSource(e.target.value)} className="w-auto min-w-[150px]">
          <option value="">All sources</option>
          {sources.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s] || s}</option>)}
        </Select>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">No matching history.</p>
      ) : (
        <ol className="space-y-0">
          {filtered.map((e) => (
            <li key={e.id} className="flex gap-3 py-3 border-b border-border last:border-0" data-testid={`vh-event-${e.id}`}>
              <span className="vda-marker mt-1.5 shrink-0" style={{ backgroundColor: "hsl(var(--vda-green))" }} aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{e.title || e.event_type}</span>
                  {e.category && <Tag tone="muted">{e.category}</Tag>}
                  <Tag tone={SOURCE_TONE[e.source] || "muted"}>{SOURCE_LABEL[e.source] || e.source}</Tag>
                </div>
                {e.body && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap break-words">{e.body}</p>}
                {e.raw_content && e.raw_content !== e.body && (
                  <details className="mt-1">
                    <summary className="text-xs text-[hsl(var(--vda-quiet))] cursor-pointer">Original note</summary>
                    <pre className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap break-words font-mono bg-secondary/40 p-2 rounded-[6px]">{e.raw_content}</pre>
                  </details>
                )}
                <p className="text-[11px] font-mono text-muted-foreground mt-1">
                  {fmtDateTime(e.event_at)}{e.actor_label ? ` · ${e.actor_label}` : ""}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
